import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Search } from 'lucide-react';

export default function SearchableSelect({
	options = [],
	value = '',
	onChange,
	placeholder = '— اختر —',
	style = {},
	required = false
}) {
	const [isOpen, setIsOpen] = useState(false);
	const [search, setSearch] = useState('');
	const wrapperRef = useRef(null);

	// Find the selected option's label
	const selectedOption = options.find(opt => String(opt.value) === String(value));
	const displayLabel = selectedOption ? selectedOption.label : '';

	// Close dropdown if clicked outside
	useEffect(() => {
		function handleClickOutside(event) {
			if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
				setIsOpen(false);
			}
		}
		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, []);

	// Filter options
	const filteredOptions = options.filter(opt =>
		(opt.label || '').toLowerCase().includes(search.toLowerCase())
	);

	return (
		<div ref={wrapperRef} style={{ position: 'relative', width: '100%' }}>
			<div
				onClick={() => setIsOpen(!isOpen)}
				style={{
					...style,
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
					cursor: 'pointer',
					userSelect: 'none'
				}}
			>
				<span style={{ color: displayLabel ? 'inherit' : '#9CA3AF', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
					{displayLabel || placeholder}
				</span>
				<ChevronDown size={14} style={{ color: '#9CA3AF' }} />
			</div>

			{isOpen && (
				<div style={{
					position: 'absolute',
					top: '100%',
					left: 0,
					right: 0,
					marginTop: '4px',
					backgroundColor: 'var(--bg-flat-white, #FFFFFF)',
					border: '1px solid var(--border-light, #E2E8F0)',
					borderRadius: '8px',
					boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
					zIndex: 50,
					maxHeight: '250px',
					display: 'flex',
					flexDirection: 'column',
					overflow: 'hidden'
				}}
					className="dark:!bg-gray-800 dark:!border-gray-700 dark:!text-gray-100"
				>
					<div style={{ padding: '8px', borderBottom: '1px solid var(--border-light, #E2E8F0)' }} className="dark:!border-gray-700">
						<div style={{ position: 'relative' }}>
							<Search size={14} style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', color: '#9CA3AF' }} />
							<input
								type="text"
								autoFocus
								placeholder="بحث..."
								value={search}
								onChange={e => setSearch(e.target.value)}
								style={{
									width: '100%',
									padding: '6px 28px 6px 8px',
									borderRadius: '6px',
									border: '1px solid var(--border-light, #E2E8F0)',
									fontSize: '13px',
									fontFamily: 'Tajawal, sans-serif',
									outline: 'none',
									backgroundColor: 'var(--bg-flat-50, #F8FAFC)'
								}}
								className="dark:!bg-gray-900 dark:!border-gray-600 dark:!text-gray-100"
							/>
						</div>
					</div>
					<div style={{ overflowY: 'auto', flex: 1, padding: '4px 0' }}>
						{filteredOptions.length === 0 ? (
							<div style={{ padding: '8px 12px', fontSize: '13px', color: '#9CA3AF', textAlign: 'center' }}>
								لا توجد نتائج
							</div>
						) : (
							filteredOptions.map((opt) => (
								<div
									key={opt.value}
									onClick={() => {
										onChange(opt.value);
										setIsOpen(false);
										setSearch(''); // Reset search after selection
									}}
									style={{
										padding: '8px 12px',
										fontSize: '13px',
										cursor: 'pointer',
										backgroundColor: String(value) === String(opt.value) ? 'var(--blue-50, #EFF6FF)' : 'transparent',
										color: String(value) === String(opt.value) ? 'var(--blue-700, #1D4ED8)' : 'inherit',
										fontWeight: String(value) === String(opt.value) ? '600' : '400'
									}}
									onMouseEnter={(e) => {
										if (String(value) !== String(opt.value)) {
											e.target.style.backgroundColor = 'var(--bg-flat-50, #F8FAFC)';
										}
									}}
									onMouseLeave={(e) => {
										if (String(value) !== String(opt.value)) {
											e.target.style.backgroundColor = 'transparent';
										}
									}}
									className={String(value) === String(opt.value) ? "dark:!bg-blue-900/30 dark:!text-blue-300" : "hover:dark:!bg-gray-700"}
								>
									{opt.label}
								</div>
							))
						)}
					</div>
				</div>
			)}
			{/* Hidden select strictly for required validation if needed, though custom validation might be preferred, this is a simple hack */}
			{required && (
				<select
					value={value}
					onChange={() => { }}
					required
					style={{ opacity: 0, position: 'absolute', top: 0, left: 0, height: '100%', width: '100%', zIndex: -1, pointerEvents: 'none' }}
				>
					<option value=""></option>
					<option value={value}>{value}</option>
				</select>
			)}
		</div>
	);
}
